import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { INBOX_VIEWS, type AgentPolicy, type EscalationLadder, type InboxStats, type InboxView } from '@unipostudio/shared-types';
import { useInboxMessages } from '@/shared/hooks/useInbox';
import { INBOX_POLL_MS } from '@/shared/lib/inbox-poll';
import { useAuth } from '@/shared/hooks/useAuth';
import { DEFAULT_LADDER, DEFAULT_POLICY } from '@/shared/services/mock/inbox-agent.mock';
import { inboxService, type ConversationListParams } from '@/shared/services/inbox.service';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { useAgentPolicy, useAgentReadAllowed, useConversationStates, useEscalationLadder } from '@/shared/hooks/useInboxAgent';
import { buildConversations, conversationsFromSummaries, type Conversation } from '@/shared/lib/conversations';

export interface ConversationData {
  /** The loaded conversations (and the open one), newest first. */
  all: Conversation[];
  /** The list's rows only: the loaded pages, without an open conversation from elsewhere. */
  listed: Conversation[];
  /** How many conversations each view holds: the server's count over every conversation. */
  counts: Record<InboxView, number>;
  stats: InboxStats;
  policy: AgentPolicy;
  ladder: EscalationLadder;
  meId: string | null;
  unreadCount: number;
  /** Open conversations per assignee, over every conversation (the server's). */
  loads: Record<string, number>;
  isLoading: boolean;
  isError: boolean;
  /** Re-renders once a minute so SLA countdowns move without refetching. */
  now: number;
  /** The server has more conversations than those loaded. */
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

/** What the inbox narrows the list by; all of it is applied by the server. */
export interface ConversationQuery {
  /** One view only; omitted: every conversation. */
  view?: InboxView | null;
  search?: string;
  /** SCREAMING_CASE platform, as the API enum. */
  platform?: string | null;
  /** Conversations with a customer message in this range (ISO). */
  dateFrom?: string | null;
  dateTo?: string | null;
  memberId?: string | null;
  levelId?: string | null;
  /**
   * The open conversation: its whole thread is fetched as well, so a DM
   * reads in full whatever the list row carries.
   */
  threadKey?: string | null;
}

/** Conversations per page; the API caps a page at 100. */
export const INBOX_PAGE_SIZE = 30;

const ZERO_COUNTS = Object.fromEntries(INBOX_VIEWS.map((v) => [v, 0])) as Record<InboxView, number>;
const NO_STATS: InboxStats = {
  resolvedByAi: { value: 0, delta: null },
  medianFirstResponseMinutes: { value: 0, delta: null },
  openAtHumanLevels: { value: 0, delta: null },
  slaBreachesThisWeek: { value: 0, delta: null },
};

function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/** What the inbox assumes when the plan has no agent: every conversation goes to a person. */
const AGENT_OFF_POLICY: AgentPolicy = { ...DEFAULT_POLICY, defaultMode: 'off', perChannel: {} };

/**
 * The inbox's conversations from the server (GET /inbox/conversations),
 * for any screen that needs them (inbox, overview, calendar rail). Views,
 * counts, search and SLA are the server's, over every conversation of the
 * brand, not only what is loaded (IA-05, IA-06, IA-10). A page holds 30
 * conversations; more load on request.
 */
export function useConversationData(query: ConversationQuery = {}): ConversationData {
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const now = useMinuteClock();
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);

  const params = useMemo<ConversationListParams>(
    () => ({
      view: query.view ?? null,
      q: query.search?.trim() || null,
      platform: query.platform ?? null,
      dateFrom: query.dateFrom ?? null,
      dateTo: query.dateTo ?? null,
      memberId: query.memberId ?? null,
      levelId: query.levelId ?? null,
      limit: INBOX_PAGE_SIZE,
    }),
    [query.view, query.search, query.platform, query.dateFrom, query.dateTo, query.memberId, query.levelId],
  );
  const pages = useInfiniteQuery({
    queryKey: ['inbox', 'conversations', params, wsId],
    queryFn: ({ pageParam }) => inboxService.listConversations({ ...params, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!wsId,
    refetchInterval: INBOX_POLL_MS,
    refetchIntervalInBackground: false,
    // A new search or view keeps the old rows until the new ones arrive: no skeleton per keystroke.
    placeholderData: keepPreviousData,
  });
  const stats = useQuery({
    queryKey: ['inbox', 'conversation-stats', wsId],
    queryFn: () => inboxService.conversationStats(7),
    enabled: !!wsId,
    staleTime: 60_000,
  });
  const threadKey = query.threadKey ?? null;
  const thread = useInboxMessages(
    { conversation: threadKey ?? '', limit: 100, isArchived: false },
    { enabled: !!threadKey, poll: true },
  );
  const summaries = useMemo(() => (pages.data?.pages ?? []).flatMap((p) => p.data), [pages.data]);
  const threadListed = !!threadKey && summaries.some((s) => s.key === threadKey);
  // An open conversation that is not on a loaded page (a deep link, another
  // view): its state is asked for by key, never every state of the brand.
  const threadState = useConversationStates(threadKey && !threadListed ? [threadKey] : []);

  const policyQuery = useAgentPolicy();
  const ladderQuery = useEscalationLadder();

  // On the free Starter plan the agent takes no conversations, and the API
  // does not hand out a policy.
  const agentAllowed = useAgentReadAllowed('classic');
  const policy = policyQuery.data ?? (agentAllowed ? DEFAULT_POLICY : AGENT_OFF_POLICY);
  const ladder = ladderQuery.data ?? DEFAULT_LADDER;

  const all = useMemo(() => {
    const list = conversationsFromSummaries({
      summaries,
      threadKey,
      threadEvents: thread.data?.data ?? [],
      policy,
      ladder,
      now,
    });
    if (threadKey && !threadListed && (thread.data?.data.length ?? 0) > 0) {
      const extra = buildConversations({ events: thread.data?.data ?? [], states: threadState.data ?? {}, policy, ladder, now })
        .filter((c) => c.key === threadKey);
      list.push(...extra);
    }
    return list;
  }, [summaries, threadKey, threadListed, thread.data, threadState.data, policy, ladder, now]);

  const listed = useMemo(() => all.filter((c) => summaries.some((s) => s.key === c.key)), [all, summaries]);
  const first = pages.data?.pages[0];
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = pages;
  return {
    all,
    listed,
    counts: first?.counts ?? ZERO_COUNTS,
    // A partial answer (an older API) shows zeros rather than breaking the strip.
    stats: stats.data?.resolvedByAi && stats.data.medianFirstResponseMinutes && stats.data.openAtHumanLevels && stats.data.slaBreachesThisWeek ? stats.data : NO_STATS,
    policy,
    ladder,
    meId,
    unreadCount: first?.unreadCount ?? 0,
    loads: first?.loads ?? {},
    isLoading: pages.isLoading,
    isError: pages.isError,
    now,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    },
  };
}

/**
 * One customer's conversations, for the customer drawer: asked of the
 * server by customer key, so a past conversation shows however old it is.
 */
export function useCustomerConversations(customerKey: string | null, enabled: boolean): Conversation[] | null {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const now = useMinuteClock();
  const agentAllowed = useAgentReadAllowed('classic');
  const policyQuery = useAgentPolicy();
  const ladderQuery = useEscalationLadder();
  const policy = policyQuery.data ?? (agentAllowed ? DEFAULT_POLICY : AGENT_OFF_POLICY);
  const ladder = ladderQuery.data ?? DEFAULT_LADDER;
  const query = useQuery({
    queryKey: ['inbox', 'conversations', 'customer', customerKey, wsId],
    queryFn: () => inboxService.listConversations({ customerKey, limit: 100 }),
    enabled: !!wsId && !!customerKey && enabled,
  });
  return useMemo(
    () => (query.data ? conversationsFromSummaries({ summaries: query.data.data, policy, ladder, now }) : null),
    [query.data, policy, ladder, now],
  );
}
